const PROMO_SELECTOR = '[data-labor-day-promo]';
const CART_MUTATION_PATTERN = /\/cart\/(add|change|update)(\.js)?(?:\?|$)/;
const nativeFetch = typeof window === 'undefined' ? null : window.fetch.bind(window);
const HTMLElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

function parseCents(value) {
  const cents = Number.parseInt(value, 10);
  return Number.isFinite(cents) ? Math.max(0, cents) : 0;
}

function parsePromoDate(value) {
  const timestamp = Date.parse(value || '');
  return Number.isFinite(timestamp) ? timestamp : null;
}

export function shouldDisplayPromo(element, now = Date.now()) {
  if (element.dataset.enabled !== 'true') return false;

  const start = parsePromoDate(element.dataset.start);
  const end = parsePromoDate(element.dataset.end);
  if (start === null || end === null || end < start) return false;

  if (element.dataset.themeRole !== 'main') return true;
  return now >= start && now <= end;
}

export function getTierState(subtotal, thresholds, savings) {
  const safeSubtotal = Math.max(0, subtotal);
  const maxThreshold = Math.max(1, thresholds[2]);
  const progress = Math.min(100, (safeSubtotal / maxThreshold) * 100);

  if (safeSubtotal >= thresholds[2]) {
    return { progress, tier: 3, saving: savings[2], remaining: 0, nextSaving: 0 };
  }
  if (safeSubtotal >= thresholds[1]) {
    return {
      progress,
      tier: 2,
      saving: savings[1],
      remaining: thresholds[2] - safeSubtotal,
      nextSaving: savings[2],
    };
  }
  if (safeSubtotal >= thresholds[0]) {
    return {
      progress,
      tier: 1,
      saving: savings[0],
      remaining: thresholds[1] - safeSubtotal,
      nextSaving: savings[1],
    };
  }
  return {
    progress,
    tier: 0,
    saving: savings[0],
    remaining: thresholds[0] - safeSubtotal,
    nextSaving: savings[0],
  };
}

export function formatPromoMoney(cents, currency, locale) {
  try {
    const options = {
      style: 'currency',
      currency: currency || 'USD',
    };
    const maximumFractionDigits = new Intl.NumberFormat(locale || 'en', options).resolvedOptions()
      .maximumFractionDigits;
    return new Intl.NumberFormat(locale || 'en', {
      ...options,
      minimumFractionDigits: 0,
      maximumFractionDigits,
    }).format(cents / 100);
  } catch (_error) {
    return `${(cents / 100).toFixed(2)} ${currency || ''}`.trim();
  }
}

function interpolate(template, values) {
  return Object.entries(values).reduce(
    (copy, [key, value]) => copy.replaceAll(`[${key}]`, value),
    template || '',
  );
}

function promoSchedule(element) {
  const thresholds = [
    parseCents(element.dataset.thresholdOne),
    parseCents(element.dataset.thresholdTwo),
    parseCents(element.dataset.thresholdThree),
  ];
  const savings = [
    parseCents(element.dataset.savingOne),
    parseCents(element.dataset.savingTwo),
    parseCents(element.dataset.savingThree),
  ];
  return { thresholds, savings };
}

function applyPromoVisibility(element) {
  const visible = shouldDisplayPromo(element);
  element.hidden = !visible;
  element.classList.remove('labor-day-promo-pending');
  return visible;
}

class LaborDayTieredSale extends HTMLElementBase {
  connectedCallback() {
    this.currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
    this.locale = document.documentElement.lang || window.Shopify?.locale || 'en';
    this.render(parseCents(this.dataset.subtotal));
    applyPromoVisibility(this);
    refreshCartSoon();
  }

  render(subtotal) {
    const { thresholds, savings } = promoSchedule(this);
    const state = getTierState(subtotal, thresholds, savings);
    const money = (value) => formatPromoMoney(value, this.currency, this.locale);

    this.querySelectorAll('[data-tier]').forEach((tierElement, index) => {
      const unlocked = subtotal >= thresholds[index];
      tierElement.classList.toggle('is-unlocked', unlocked);
      tierElement.querySelector('[data-threshold]').textContent = money(thresholds[index]);
      tierElement.querySelector('[data-saving]').textContent = money(savings[index]);
    });

    this.querySelectorAll('[data-marker]').forEach((marker, index) => {
      const position = Math.min(100, (thresholds[index] / Math.max(1, thresholds[2])) * 100);
      marker.style.left = `${position}%`;
      marker.classList.toggle('is-unlocked', subtotal >= thresholds[index]);
      marker.querySelector('.visually-hidden').textContent = money(thresholds[index]);
    });

    const progress = this.querySelector('[data-progress]');
    progress.setAttribute('aria-valuemax', String(thresholds[2]));
    progress.setAttribute('aria-valuenow', String(Math.min(subtotal, thresholds[2])));
    this.querySelector('[data-progress-fill]').style.width = `${state.progress}%`;

    let primaryMessage;
    let secondaryMessage;
    if (state.tier === 0) {
      primaryMessage = interpolate(this.dataset.beforeFirstTitle, {
        saving: money(state.saving),
      });
      secondaryMessage = interpolate(this.dataset.beforeFirst, {
        amount: money(state.remaining),
        saving: money(state.saving),
      });
    } else if (state.tier === 3) {
      primaryMessage = interpolate(this.dataset.bestTitle, { saving: money(state.saving) });
      secondaryMessage = interpolate(this.dataset.best, { saving: money(state.saving) });
    } else {
      primaryMessage = interpolate(this.dataset.unlockedTitle, {
        saving: money(state.saving),
      });
      secondaryMessage = interpolate(this.dataset.unlockedNext, {
        amount: money(state.remaining),
        next_saving: money(state.nextSaving),
      });
    }

    const message = this.querySelector('[data-message]');
    message.querySelector('[data-message-primary]').textContent = primaryMessage;
    message.querySelector('[data-message-secondary]').textContent = secondaryMessage;
    message.setAttribute('aria-label', `${primaryMessage}. ${secondaryMessage}`);
    this.dataset.subtotal = String(subtotal);
  }
}

let cartRefreshTimer;
let cartRefreshPromise;

function refreshCartSoon() {
  if (typeof window === 'undefined') return;
  window.clearTimeout(cartRefreshTimer);
  cartRefreshTimer = window.setTimeout(refreshCart, 40);
}

async function refreshCart() {
  const trackers = [...document.querySelectorAll('labor-day-tiered-sale')];
  if (!trackers.length || !nativeFetch) return;
  if (cartRefreshPromise) return cartRefreshPromise;

  cartRefreshPromise = nativeFetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  })
    .then((response) => {
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      return response.json();
    })
    .then((cart) => {
      const subtotal = parseCents(cart.original_total_price);
      document.querySelectorAll('labor-day-tiered-sale').forEach((tracker) => {
        tracker.currency = cart.currency || tracker.currency;
        tracker.render(subtotal);
        applyPromoVisibility(tracker);
      });
    })
    .catch((error) => console.warn('Labor Day tracker could not refresh the cart.', error))
    .finally(() => {
      cartRefreshPromise = null;
    });

  return cartRefreshPromise;
}

function syncPromoVisibility() {
  if (typeof document === 'undefined') return;
  document.querySelectorAll(PROMO_SELECTOR).forEach(applyPromoVisibility);
}

function scheduleVisibilityCheck() {
  if (typeof document === 'undefined') return;
  const mainPromos = [...document.querySelectorAll(PROMO_SELECTOR)].filter(
    (element) => element.dataset.themeRole === 'main' && element.dataset.enabled === 'true',
  );
  const futureBoundaries = mainPromos
    .flatMap((element) => [parsePromoDate(element.dataset.start), parsePromoDate(element.dataset.end)])
    .filter((timestamp) => timestamp !== null && timestamp >= Date.now())
    .sort((a, b) => a - b);

  if (!futureBoundaries.length) return;
  const delay = Math.min(2_147_000_000, Math.max(1_000, futureBoundaries[0] - Date.now() + 1_000));
  window.setTimeout(() => {
    syncPromoVisibility();
    scheduleVisibilityCheck();
  }, delay);
}

if (typeof window !== 'undefined') {
  if (!customElements.get('labor-day-tiered-sale')) {
    customElements.define('labor-day-tiered-sale', LaborDayTieredSale);
  }

  window.fetch = async (...args) => {
    const response = await nativeFetch(...args);
    const input = args[0];
    const url = typeof input === 'string' ? input : input?.url || '';
    if (CART_MUTATION_PATTERN.test(url)) refreshCartSoon();
    return response;
  };

  document.addEventListener('DOMContentLoaded', () => {
    syncPromoVisibility();
    refreshCartSoon();
    scheduleVisibilityCheck();
  });
  document.addEventListener('shopify:section:load', () => {
    syncPromoVisibility();
    refreshCartSoon();
  });
  document.addEventListener('product:added-to-cart', refreshCartSoon);
  window.addEventListener('pageshow', refreshCartSoon);
}
